import { useState, useMemo } from 'react';

/**
 * A React hook for managing server-side pagination.
 *
 * @param {number} initialPage - The initial page number.
 * @param {number} initialTotalItems - The initial total number of items.
 * @param {number} initialItemsPerPage - The initial number of items per page.
 * @returns {{
 * currentPage: number,
 * totalPages: number,
 * setCurrentPage: (page: number) => void,
 * itemsPerPage: number,
 * setItemsPerPage: (items: number) => void,
 * totalItems: number,
 * setTotalItems: (total: number) => void,
 * }} - An object containing the current page, total pages, a function to set the current page, items per page, a function to set items per page, the total items, and a function to set total items.
 */
const usePagination = (
  initialPage = 1,
  initialTotalItems = 0,
  initialItemsPerPage = 15,
): {
    currentPage: number;
    totalPages: number;
    setCurrentPage: (page: number) => void;
    itemsPerPage: number;
    setItemsPerPage: (items: number) => void;
    totalItems: number;
    setTotalItems: (total: number) => void;
  } => {
  const [currentPage, setCurrentPage] = useState<number>(Math.max(1, initialPage)); // Ensure initialPage is at least 1
  const [itemsPerPage, setItemsPerPage] = useState<number>(initialItemsPerPage);
  const [totalItems, setTotalItems] = useState<number>(initialTotalItems);

  const totalPages = useMemo(() => (
    Math.ceil(totalItems / itemsPerPage)
  ), [totalItems, itemsPerPage]);

  const setPage = (page: number): void => {
    if (page < 1) {
      setCurrentPage(1);
      return;
    }
    if (page > totalPages) {
      setCurrentPage(totalPages > 0 ? totalPages : 1); // Ensure currentPage is 1 if totalPages is 0
      return;
    }
    setCurrentPage(page);
  };

  const handleSetItemsPerPage = (items: number) => {
    setItemsPerPage((prevItems) => {
      if (items !== prevItems) { setCurrentPage(1); }
      return items;
    });
  };

  const handleSetTotalItems = (total: number) => {
    setTotalItems((prevTotal) => {
      const prevTotalPages = Math.ceil(prevTotal / itemsPerPage);
      const newTotalPages = Math.ceil(total / itemsPerPage);
      if (newTotalPages !== prevTotalPages) { setCurrentPage(1); }
      return total;
    });
  };

  return {
    currentPage,
    totalPages: Math.max(1, totalPages), // Ensure totalPages is at least 1
    setCurrentPage: setPage,
    itemsPerPage,
    setItemsPerPage: handleSetItemsPerPage,
    totalItems,
    setTotalItems: handleSetTotalItems,
  };
};

export default usePagination;
